import React from 'react';
import {observer} from 'mobx-react';
import LineChart from '../charts/LineChart';
import {AnalyticsTab, DiagramMode, Store} from '../../stores/store';
import {BaseChartData, KpiPackage, TooltipComparisonMode, VisibleAttribute} from '../../types/Chart';
import BarChart from '../charts/BarChart';
import _ from 'lodash';
import {ColorHelper, px} from 'csx';
import {classes, stylesheet} from 'typestyle';
import {DIAGRAM_COLORS} from '../../utility/ColorUtils';
import {isKPI, KPI} from '../../kpi';
import {I18n} from '../../i18n/i18n';
import {DraggedCellMode, DraggedTableCellData, isDraggedCellMode} from '../Table/SpecialActionTableCell';
import {CompareMode} from '../../stores/CompareStore';
import {GreyBlueBackgroundColor, LightGreyColor, WhiteColorFaded90} from '../../styles/colors';

interface SearchProductDiagramProps {
    store: Store;
}

@observer
export default class SearchProductDiagram extends React.Component<SearchProductDiagramProps, {}> {
    public handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.nativeEvent.preventDefault();
    };

    private isDraggedTableCellData(value: any): value is DraggedTableCellData {
        const kpiValid = value.secondary && isKPI(value.secondary);
        const compareChannelValid = value.compareTarget === undefined || typeof value.compareTarget === 'string';
        return (
            isDraggedCellMode(value.draggedCellMode) &&
            typeof value.primary === 'string' &&
            typeof value.title === 'string' &&
            kpiValid &&
            compareChannelValid
        );
    }

    private addToChannelDiagram(draggedTermJson: DraggedTableCellData, colorIndex?: number): void {
        const {compareStore, lineDiagramStore} = this.props.store;
        const {compareIsEnabled, compareData} = compareStore;
        const {variantChannelCompare, channelCompare} = compareData;

        if (compareIsEnabled) {
            if (channelCompare) {
                if (draggedTermJson.compareMode && draggedTermJson.compareMode === CompareMode.ChannelOrVariantColumn) {
                    lineDiagramStore.addChannelToDiagram(
                        draggedTermJson.compareTarget || '',
                        draggedTermJson.secondary,
                        draggedTermJson.title,
                        colorIndex
                    );
                    return;
                }
                channelCompare.channelNames.forEach((channelName) =>
                    lineDiagramStore.addChannelToDiagram(channelName, draggedTermJson.secondary, draggedTermJson.title)
                );
                return;
            } else if (variantChannelCompare) {
                if (draggedTermJson.compareMode && draggedTermJson.compareMode === CompareMode.ChannelOrVariantColumn) {
                    lineDiagramStore.addChannelToDiagram(
                        draggedTermJson.primary,
                        draggedTermJson.secondary,
                        draggedTermJson.title,
                        colorIndex,
                        variantChannelCompare.testId,
                        draggedTermJson.compareTarget
                    );
                    return;
                }
                variantChannelCompare.variantIds.forEach((variantId) =>
                    lineDiagramStore.addChannelToDiagram(
                        draggedTermJson.primary,
                        draggedTermJson.secondary,
                        draggedTermJson.title,
                        undefined,
                        variantChannelCompare.testId,
                        variantId
                    )
                );
                return;
            }
        }
        lineDiagramStore.addChannelToDiagram(draggedTermJson.primary, draggedTermJson.secondary, draggedTermJson.title, colorIndex);
    }

    private addToNormalDiagram(draggedTermJson: DraggedTableCellData, colorIndex?: number): void {
        const {compareStore, lineDiagramStore, channelStore} = this.props.store;
        const {compareIsEnabled, compareData} = compareStore;
        const {variantChannelCompare, channelCompare} = compareData;
        const selectedChannel = channelStore.selectedChannel ? channelStore.selectedChannel.name : '';
        if (compareIsEnabled) {
            if (channelCompare) {
                if (draggedTermJson.compareMode && draggedTermJson.compareMode === CompareMode.ChannelOrVariantColumn) {
                    lineDiagramStore.addTermToDiagram(
                        draggedTermJson.primary,
                        draggedTermJson.secondary,
                        draggedTermJson.title,
                        draggedTermJson.compareTarget || '',
                        colorIndex
                    );
                    return;
                }
                channelCompare.channelNames.forEach((channelName) =>
                    lineDiagramStore.addTermToDiagram(draggedTermJson.primary, draggedTermJson.secondary, draggedTermJson.title, channelName)
                );
                return;
            } else if (variantChannelCompare) {
                if (draggedTermJson.compareMode && draggedTermJson.compareMode === CompareMode.ChannelOrVariantColumn) {
                    lineDiagramStore.addTermToDiagram(
                        draggedTermJson.primary,
                        draggedTermJson.secondary,
                        draggedTermJson.title,
                        selectedChannel,
                        colorIndex,
                        variantChannelCompare.testId,
                        draggedTermJson.compareTarget
                    );
                    return;
                }
                variantChannelCompare.variantIds.forEach((variantId) =>
                    lineDiagramStore.addTermToDiagram(
                        draggedTermJson.primary,
                        draggedTermJson.secondary,
                        draggedTermJson.title,
                        selectedChannel,
                        undefined,
                        variantChannelCompare.testId,
                        variantId
                    )
                );
                return;
            }
        }
        lineDiagramStore.addTermToDiagram(draggedTermJson.primary, draggedTermJson.secondary, draggedTermJson.title, selectedChannel, colorIndex);
    }

    public handleDropOnDiagram = (e: React.DragEvent<HTMLDivElement>) => {
        /**
         * Either request a channel or a searchterm addition to the diagram
         */
        e.preventDefault();
        e.stopPropagation();
        const target = e.currentTarget;
        const draggedTerm = e.dataTransfer.getData('draggedCellData');
        if (draggedTerm === '') {
            return;
        }
        const draggedTermJson = JSON.parse(draggedTerm);

        if (!this.isDraggedTableCellData(draggedTermJson)) {
            return;
        }

        const colorIndexValue = target.getAttribute('data-color_index');
        const colorIndex = colorIndexValue === null ? undefined : parseInt(colorIndexValue, 10);

        switch (draggedTermJson.draggedCellMode) {
            case DraggedCellMode.SEARCH_PRODUCT:
                this.addToNormalDiagram(draggedTermJson, colorIndex);
                return;
            case DraggedCellMode.CHANNEL:
                this.addToChannelDiagram(draggedTermJson, colorIndex);
                return;
            default:
                return;
        }
    };

    public generateAvailableColorBoxes() {
        return DIAGRAM_COLORS.map((color: ColorHelper, i) => {
            if (this.props.store.lineDiagramStore.colorIndexAlreadyUsed(i)) {
                return <div key={i} data-color_index={i} className={diagramCss.colorDropBox} style={{backgroundColor: LightGreyColor}} />;
            } else {
                return (
                    <div
                        key={i}
                        data-color_index={i}
                        className={diagramCss.colorDropBox}
                        onDrop={this.handleDropOnDiagram.bind(this)}
                        style={{backgroundColor: color.toString()}}
                    />
                );
            }
        });
    }

    public insertColorDropBoxes() {
        if (!this.props.store.lineDiagramStore.coloredDropBoxesAreVisible) {
            return null;
        }
        return (
            <div id="color_drop_box_container" className={diagramCss.colorDropBoxContainer}>
                {this.generateAvailableColorBoxes()}
            </div>
        );
    }

    public renderEmptyDiagramDropArea() {
        /**
         * Either shows the empty drop area if no diagram data is available, or shows the hidden drop hover needed on mobile devices
         * @type {null}
         */
        const placeholderHeight = this.props.store.windowHeight * 0.4 - 54;
        if (this.props.store.lineDiagramStore.diagramData.length === 0) {
            return (
                <div
                    className={diagramCss.emptyDiagram}
                    style={{
                        height: placeholderHeight,
                        position: 'relative',
                    }}
                    onDragOver={this.handleDragOver}
                    onDrop={this.handleDropOnDiagram}>
                    <div className={diagramCss.channelDropMessage}>
                        <I18n i18nKey="chart:noChartSelectedText" />
                    </div>
                    {this.insertColorDropBoxes()}
                </div>
            );
        }

        const hiddenHoverClassName = this.props.store.lineDiagramStore.coloredDropBoxesAreVisible
            ? classes(diagramCss.emptyDiagramHiddenHover, 'visible')
            : diagramCss.emptyDiagramHiddenHover;

        return (
            <div
                id="hidden_drop_hover_line_diagram"
                className={hiddenHoverClassName}
                style={{minHeight: placeholderHeight}}
                onDragOver={this.handleDragOver}
                onDrop={this.handleDropOnDiagram}>
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                    }}>
                    {this.insertColorDropBoxes()}
                </div>
            </div>
        );
    }

    private getLegendItemPosition(data: VisibleAttribute): number {
        const {channel, isChannelDiagram, kpi, searchtermOrProductId} = data.kpiRequestInfo;
        const {lineDiagramStore} = this.props.store;

        if (isChannelDiagram) {
            return lineDiagramStore.requestedChannelDiagrams.findIndex(
                (channelDiagram) => channelDiagram.kpi === kpi && channelDiagram.channelName === channel.name
            );
        } else {
            const diagramIndex = lineDiagramStore.requestedNormalDiagrams.findIndex(
                (diagram) => diagram.kpi === kpi && diagram.channelName === channel.name && diagram.searchtermOrProductId === searchtermOrProductId
            );
            return diagramIndex + lineDiagramStore.requestedChannelDiagrams.length;
        }
    }

    private getBarAttributes(): VisibleAttribute[] {
        const {lineDiagramStore} = this.props.store;
        const {checkIfKpiRequiresMoneyFormat} = this.props.store.selectorTableStore;
        const visibleAttributes: VisibleAttribute[] = lineDiagramStore.loadedDiagramData.map((loadedDiagram) => {
            return {
                color: lineDiagramStore.getDiagramColor(
                    loadedDiagram.channel.name,
                    loadedDiagram.searchtermOrProductId,
                    loadedDiagram.kpi,
                    loadedDiagram.testId,
                    loadedDiagram.variantId
                ),
                opacity: lineDiagramStore.getOpacityForChannelTerm(
                    loadedDiagram.channel,
                    loadedDiagram.searchtermOrProductId,
                    loadedDiagram.kpi,
                    loadedDiagram.testId,
                    loadedDiagram.variantId
                ),
                moneyFormatting: checkIfKpiRequiresMoneyFormat(loadedDiagram.kpi),
                kpiRequestInfo: loadedDiagram,
                useAbsValue: lineDiagramStore.diagramYScaleAbsolute,
            };
        });

        return _.sortBy<VisibleAttribute>(visibleAttributes, [
            (data) => this.getLegendItemPosition(data),
            (data) => data.kpiRequestInfo.isCompareDiagram,
        ]);
    }

    private getAttributeNameIfPossible(kpi: KPI): string {
        return this.props.store.selectorTableStore.getAttributeNameIfPossible(kpi);
    }

    private switchCampaignIdWithName = (searchtermOrProductId: string): string => {
        const {activeAnalyticsTab, lineDiagramStore} = this.props.store;
        if (activeAnalyticsTab === AnalyticsTab.CampaignView) {
            const diagramMatch = lineDiagramStore.requestedNormalDiagrams.find(
                (normalDiagram) => normalDiagram.searchtermOrProductId === searchtermOrProductId
            );
            if (diagramMatch && diagramMatch.title) {
                return diagramMatch.title;
            }
        }
        return searchtermOrProductId;
    };

    private getNameForChannelTerm = (kpiPackage: KpiPackage): React.ReactNode => {
        const {isChannelDiagram, searchtermOrProductId, channel, kpi, isCompareDiagram, testId, variantId} = kpiPackage;

        const addTestAndVariant = () => (testId && variantId ? ` - ${variantId}` : '');

        let text = '';
        if (isChannelDiagram) {
            text = `${this.getAttributeNameIfPossible(kpi)} - ${channel.name}` + addTestAndVariant();
        } else {
            text =
                `${this.getAttributeNameIfPossible(kpi)} - ${this.switchCampaignIdWithName(searchtermOrProductId)} - ${channel.name}` +
                addTestAndVariant();
        }

        const textStyle = {display: 'inline'};
        if (isCompareDiagram) {
            textStyle['textDecorationStyle'] = 'dashed';
            textStyle['textDecorationLine'] = 'underline';
        }
        return <div style={textStyle}>{text}</div>;
    };

    /**
     * If a rate is displayed or a relative diagram is displayed, compare mod is disabled.
     * If identical kpis are displayed, comparison values should be shown in percent.
     * If different kpis are displayed, absolute values should be shown as comparison
     * @returns {TooltipComparisonMode}
     */
    private getTooltipComparisonMode(): TooltipComparisonMode {
        const {diagramData, diagramYScaleAbsolute} = this.props.store.lineDiagramStore;

        if (!diagramYScaleAbsolute) {
            return TooltipComparisonMode.Disabled;
        }

        if (diagramData.length > 0) {
            const data = diagramData[0].data;
            const containsRates = data.some((single) => single.isRate || false);
            const kpisUsed: string[] = _.uniq(data.map((single) => single.kpi));

            if (containsRates) {
                return TooltipComparisonMode.Disabled;
            }

            if (kpisUsed.length === 1) {
                return TooltipComparisonMode.Percent;
            }

            if (kpisUsed.length > 1) {
                return TooltipComparisonMode.Absolute;
            }
        }
        return TooltipComparisonMode.Disabled;
    }

    private applyCurrencyExchangeRatesIfMoneyFormat<T extends BaseChartData>(chartData: T[]): T[] {
        const {applyTargetCurrencyConversionToNumber} = this.props.store.currencyStore;
        const {checkIfKpiRequiresMoneyFormat} = this.props.store.selectorTableStore;
        return chartData.map((diagramData: T) =>
            // tslint:disable-next-line:prefer-object-spread see https://stackoverflow.com/a/51193091
            Object.assign({}, diagramData, {
                data: diagramData.data.map((singleYAxisPoint) => ({
                    ...singleYAxisPoint,
                    value: checkIfKpiRequiresMoneyFormat(singleYAxisPoint.kpi)
                        ? applyTargetCurrencyConversionToNumber(singleYAxisPoint.value, singleYAxisPoint.channel.currency)
                        : singleYAxisPoint.value,
                })),
            })
        );
    }

    private renderDiagramIfDataAvailable(): React.ReactNode | null {
        const {lineDiagramStore} = this.props.store;
        const {moneyFormatToTargetCurrency} = this.props.store.currencyStore;
        const diagramWidth = this.props.store.windowWidth;
        const diagramHeight = this.props.store.windowHeight * 0.4 - 25;

        if (lineDiagramStore.diagramData.length > 0) {
            switch (this.props.store.diagramMode) {
                case DiagramMode.Daily:
                case DiagramMode.Hourly:
                    return (
                        <LineChart
                            currencyFormatting={moneyFormatToTargetCurrency()}
                            data={this.applyCurrencyExchangeRatesIfMoneyFormat(lineDiagramStore.diagramData)}
                            width={diagramWidth}
                            mainDateRange={this.props.store.timeIntervalStore.mainDateRange}
                            compareDateRange={this.props.store.timeIntervalStore.compareDateRange}
                            height={diagramHeight}
                            kpiPackageToString={this.getNameForChannelTerm}
                            visibleAttribute={lineDiagramStore.getLineAttributes()}
                            tooltipComparisonModeIfOneElementIsSelected={this.getTooltipComparisonMode()}
                            animation={true}
                        />
                    );
                case DiagramMode.TwentyFourHourMode:
                    const visibleAttributes = this.getBarAttributes();
                    return (
                        <BarChart
                            data={this.applyCurrencyExchangeRatesIfMoneyFormat(
                                lineDiagramStore.convertTo24HourAggregationToDiagramData(lineDiagramStore.diagramData, visibleAttributes)
                            )}
                            onBarClick={() => {}}
                            currencyFormatting={moneyFormatToTargetCurrency()}
                            width={diagramWidth}
                            height={diagramHeight}
                            visibleAttribute={visibleAttributes}
                            kpiPackageToString={this.getNameForChannelTerm}
                            allowBarAnimation={true}
                            tooltipComparisonModeIfOneElementIsSelected={this.getTooltipComparisonMode()}
                            vertical={true}
                        />
                    );
                default:
                    throw Error('Invalid line diagram mode selected ' + this.props.store.diagramMode);
            }
        }
        return null;
    }

    public render() {
        this.props.store.lineDiagramStore.updateDiagram;

        if (this.props.store.diagramMode === DiagramMode.None) {
            return null;
        }

        return (
            <div className={diagramCss.container}>
                <div onDragOver={this.handleDragOver} onDrop={this.handleDropOnDiagram.bind(this)}>
                    {this.renderDiagramIfDataAvailable()}
                </div>
                {this.renderEmptyDiagramDropArea()}
            </div>
        );
    }
}

const diagramCss = stylesheet({
    container: {
        position: 'relative',
        zIndex: 100,
    },
    colorDropBoxContainer: {
        width: 660,
        zIndex: 1050,
        padding: 5,
        background: WhiteColorFaded90,
        marginLeft: -330,
        position: 'absolute',
        left: '50%',
        right: '50%',
        bottom: 20,
    },
    colorDropBox: {
        borderRadius: 4,
        display: 'inline-block',
        width: 60,
        height: 60,
        marginRight: 5,
        $nest: {
            '&:last-of-type': {
                marginRight: '0 !important',
            },
        },
    },
    emptyDiagramHiddenHover: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -1,
        height: '100%',
        color: GreyBlueBackgroundColor,
        fontSize: px(20),
        textAlign: 'center',
        verticalAlign: 'middle',
        borderRadius: 2,
        $nest: {
            '&.visible': {
                zIndex: 1050,
            },
        },
    },
    emptyDiagram: {
        color: GreyBlueBackgroundColor,
        fontSize: px(20),
        fontWeight: 'bold',
        textAlign: 'center',
        verticalAlign: 'middle',
        borderRadius: 40,
        border: '8px dashed ' + GreyBlueBackgroundColor,
        margin: 20,
    },
    channelDropMessage: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translateX(-50%) translateY(-50%)',
    },
});



// WEBPACK FOOTER //
// ./src/views/Diagram/SearchProductDiagram.tsx